import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { NotificationEntity } from '../../../types/notification.types';

/**
 * The search params a tapped push lands with - see `notificationPushTarget`
 * in `pwa/swPush.ts`, which writes them. The worker cannot import this file
 * (it would drag the app graph in), so the names are repeated there.
 */
export const PUSH_PARAMS = {
  id: 'open',
  type: 'type',
  itemId: 'itemId',
  content: 'content',
} as const;

const parseContent = (content: string | null) => {
  if (!content) return null;
  try {
    const parsed = JSON.parse(content);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * A push carries `{ type, itemId, notificationId, content }`, `content` being
 * the row's `data` as a JSON string - enough to rebuild the row for
 * `notificationTarget`. Title and body play no part in where it leads.
 */
const fromParams = (params: URLSearchParams): NotificationEntity | null => {
  const id = Number(params.get(PUSH_PARAMS.id));
  const type = params.get(PUSH_PARAMS.type);
  if (!Number.isFinite(id) || id <= 0 || !type) return null;
  const itemId = Number(params.get(PUSH_PARAMS.itemId));
  return {
    id,
    type,
    itemId: Number.isFinite(itemId) ? itemId : null,
    title: '',
    body: '',
    data: parseContent(params.get(PUSH_PARAMS.content)),
    seen: false,
    seenAt: null,
    createdAt: '',
  };
};

/**
 * Open the notification a tapped push was about, the way a click in the list
 * does: marked read here (the worker has no token for it), then on to where it
 * points. Its params are dropped either way, so a type with nowhere to go
 * leaves the user on the list, and Back never runs it twice.
 */
export const useOpenFromPush = (
  open: (
    notification: NotificationEntity,
    options?: { replace?: boolean }
  ) => boolean
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pushed = fromParams(searchParams);
  const handled = useRef<number | null>(null);

  useEffect(() => {
    if (!pushed || handled.current === pushed.id) return;
    handled.current = pushed.id;
    if (open(pushed, { replace: true })) return;
    setSearchParams(
      (params) => {
        Object.values(PUSH_PARAMS).forEach((key) => params.delete(key));
        return params;
      },
      { replace: true }
    );
  }, [pushed?.id]);
};
